import { useCallback, useEffect, useState } from "react";
import { FormRouteClientType, FormRouteFormType, FormRouteParams } from "@/services/bedrock";

export type Destination = "totalexpert" | "crm" | "website";
export type FieldKey = "player" | "fan" | "client_id" | "client_email" | "lang";
export type ClientFieldKey = Exclude<FieldKey, "player">;
type TouchedKey = FieldKey | "utm_source" | "utm_medium";

export interface SpecificPlayer {
  id: number;
  email: string;
  fullName: string | null;
}

export interface BuilderState {
  destination: Destination;
  formMode: "type" | "specific";
  formType: FormRouteFormType;
  formGuid: string | null;
  playerMode: "sender" | "specific" | "none";
  specificPlayer: SpecificPlayer | null;
  enabled: Record<ClientFieldKey, boolean>;
  values: Record<FieldKey, string>;
  touched: Partial<Record<TouchedKey, boolean>>;
  clientType: FormRouteClientType;
  utm: { source: string; medium: string; campaign: string };
  codeId: number | null;
}

export const FIELD_KEYS: FieldKey[] = ["player", "fan", "client_id", "client_email", "lang"];
export const CLIENT_FIELD_KEYS: ClientFieldKey[] = ["fan", "client_id", "client_email", "lang"];

export const TOTAL_EXPERT_MERGE_FIELDS: Record<FieldKey, string> = {
  player: "{{sender.email}}", // confirmed
  fan: "{{recipient.custom.socialcoach_fan_id}}", // custom field the org creates; exact path to confirm
  client_id: "{{recipient.id}}", // confirmed with a TotalExpert admin, 2026-10-13
  client_email: "{{recipient.email}}", // confirmed with a TotalExpert admin, 2026-10-13
  lang: "{{recipient.language}}", // unconfirmed
};

export const WEBSITE_EXAMPLE_VALUES: Record<FieldKey, string> = {
  player: "sarah.lee@lender.com",
  fan: "9d3a1c2e-7b4f-4e1a-9a0b-5c6d7e8f9a01",
  client_id: "C-10422",
  client_email: "pat.jones@example.com",
  lang: "es",
};

const EMPTY_VALUES: Record<FieldKey, string> = { player: "", fan: "", client_id: "", client_email: "", lang: "" };

export const FORM_TYPE_LABELS: Record<FormRouteFormType, string> = {
  review: "Review",
  midpoint: "Mid-transaction check-in",
  bad_review_feedback: "Unhappy-client feedback",
};

/**
 * The types the builder offers. Routes still accept `bad_review_feedback`, but a CRM journey rarely
 * sends one, so the builder keeps it out of the way.
 */
export const BUILDER_FORM_TYPES: FormRouteFormType[] = ["review", "midpoint"];

/** The type's label as it reads inside a sentence ("a mid-transaction check-in form"). */
export function formTypeNoun(formType: FormRouteFormType): string {
  return FORM_TYPE_LABELS[formType].toLowerCase();
}

export function presetValues(destination: Destination): Record<FieldKey, string> {
  if (destination === "totalexpert") return TOTAL_EXPERT_MERGE_FIELDS;
  if (destination === "website") return WEBSITE_EXAMPLE_VALUES;
  return EMPTY_VALUES;
}

export function presetUtm(destination: Destination): { source: string; medium: string } {
  if (destination === "totalexpert") return { source: "totalexpert", medium: "email" };
  if (destination === "website") return { source: "website", medium: "web" };
  return { source: "crm", medium: "email" };
}

export function defaultState(): BuilderState {
  return {
    destination: "totalexpert",
    formMode: "type",
    formType: "review",
    formGuid: null,
    playerMode: "sender",
    specificPlayer: null,
    enabled: { fan: true, client_id: true, client_email: true, lang: true },
    values: { ...TOTAL_EXPERT_MERGE_FIELDS },
    touched: {},
    clientType: "customer",
    utm: { ...presetUtm("totalexpert"), campaign: "" },
    codeId: null,
  };
}

/** Untouched values take the new destination's preset; anything the admin edited stays. */
export function switchDestination(state: BuilderState, destination: Destination): BuilderState {
  const preset = presetValues(destination);
  const values = { ...state.values };
  FIELD_KEYS.forEach(key => {
    if (!state.touched[key]) values[key] = preset[key];
  });
  const utmPreset = presetUtm(destination);
  return {
    ...state,
    destination,
    values,
    utm: {
      ...state.utm,
      source: state.touched.utm_source ? state.utm.source : utmPreset.source,
      medium: state.touched.utm_medium ? state.utm.medium : utmPreset.medium,
    },
  };
}

export type ExampleKey = "past_borrowers" | "closing" | "mid_loan" | "lead_gen";

interface RouteExample {
  key: ExampleKey;
  channel: "email" | "website";
  /** Null when there's no form type to default to, so the admin picks one specific form (their lead form). */
  formType: FormRouteFormType | null;
  campaign: string;
}

/** Starting points for common journeys. Each sets only the destination, form, who's asking and campaign. */
export const ROUTE_EXAMPLES: RouteExample[] = [
  { key: "mid_loan", channel: "email", formType: "midpoint", campaign: "mid-loan-check-in" },
  { key: "closing", channel: "email", formType: "review", campaign: "post-close-review" },
  { key: "past_borrowers", channel: "email", formType: "review", campaign: "past-borrowers" },
  { key: "lead_gen", channel: "website", formType: null, campaign: "website-leads" },
];

/** Email examples keep Another CRM when it's already chosen; from Website they switch to TotalExpert. */
function exampleDestination(example: RouteExample, current: Destination): Destination {
  if (example.channel === "website") return "website";
  return current === "website" ? "totalexpert" : current;
}

export function applyExample(state: BuilderState, key: ExampleKey): BuilderState {
  const example = ROUTE_EXAMPLES.find(candidate => candidate.key === key);
  if (!example) return state;
  const switched = switchDestination(state, exampleDestination(example, state.destination));
  const form: Partial<BuilderState> = example.formType
    ? { formMode: "type", formType: example.formType }
    : { formMode: "specific", formGuid: null };
  return {
    ...switched,
    ...form,
    playerMode: "sender",
    utm: { ...switched.utm, campaign: example.campaign },
  };
}

/** The example the builder still matches, so its card stays selected until the admin changes something it set. */
export function matchingExample(state: BuilderState): ExampleKey | null {
  const match = ROUTE_EXAMPLES.find(
    example =>
      (example.channel === "website" ? state.destination === "website" : state.destination !== "website") &&
      (example.formType
        ? state.formMode === "type" && state.formType === example.formType
        : state.formMode === "specific") &&
      state.playerMode === "sender" &&
      state.utm.campaign.trim() === example.campaign
  );
  return match?.key ?? null;
}

export function hasMergeField(value: string): boolean {
  return value.includes("{{") && value.includes("}}");
}

/** Merge fields stay readable (the CRM has to find them); only their spaces are encoded. */
export function encodeParamValue(value: string): string {
  return hasMergeField(value) ? value.replace(/ /g, "%20") : encodeURIComponent(value);
}

export type RouteParamEntry = [keyof FormRouteParams, string];

export function buildRouteUrl(routeUrl: string, ref: string, params: RouteParamEntry[]): string {
  const base = `${routeUrl}/forms/${encodeURIComponent(ref)}`;
  if (!params.length) return base;
  return `${base}?${params.map(([key, value]) => `${key}=${encodeParamValue(value)}`).join("&")}`;
}

export function routeRef(state: BuilderState): string | null {
  return state.formMode === "type" ? state.formType : state.formGuid;
}

/**
 * Query parameters in the contract's order. `values` overrides the per-field values, which is how
 * the tester swaps merge fields for sample values. Never includes `test`; the tester adds it.
 */
export function routeParams(
  state: BuilderState,
  values: Partial<Record<FieldKey, string>> = state.values
): RouteParamEntry[] {
  const params: RouteParamEntry[] = [];
  const push = (key: keyof FormRouteParams, value: string | undefined | null) => {
    const trimmed = value?.trim();
    if (trimmed) params.push([key, trimmed]);
  };

  if (state.playerMode === "sender") push("player", values.player);
  if (state.playerMode === "specific") push("player", state.specificPlayer?.email);
  CLIENT_FIELD_KEYS.filter(key => key !== "lang").forEach(key => {
    if (state.enabled[key]) push(key, values[key]);
  });
  if (state.clientType === "partner") push("client_type", "partner");
  if (state.destination === "totalexpert") push("source", "TE");
  if (state.enabled.lang) push("lang", values.lang);
  push("utm_source", state.utm.source);
  push("utm_medium", state.utm.medium);
  push("utm_campaign", state.utm.campaign);
  return params;
}

export type RouteBlocker =
  | "missing_default"
  | "missing_form"
  | "missing_player"
  | "missing_merge_fields"
  | "missing_code"
  | "code_error";

export function routeBlocker(
  state: BuilderState,
  opts: {
    hasAccountDefault: boolean;
    hasCode: boolean;
    /** False when the pinned form isn't among the active forms any more (e.g. a restored route). */
    hasSpecificForm?: boolean;
    /** The codes request failed or came back empty, so "loading" would never end. */
    codeError?: boolean;
  }
) {
  if (state.formMode === "type" && !opts.hasAccountDefault) return "missing_default" as const;
  if (state.formMode === "specific" && (!state.formGuid || opts.hasSpecificForm === false)) {
    return "missing_form" as const;
  }
  if (state.playerMode === "specific" && !state.specificPlayer) return "missing_player" as const;
  // Another CRM starts blank: a route with no sender field would resolve every click to the account
  if (state.destination === "crm" && state.playerMode === "sender" && !state.values.player.trim()) {
    return "missing_merge_fields" as const;
  }
  if (!opts.hasCode) return opts.codeError ? ("code_error" as const) : ("missing_code" as const);
  return null;
}

/** Client keys that will actually be in the link, in matching order. */
export function activeClientKeys(state: BuilderState): Array<"fan" | "client_id" | "client_email"> {
  return (["fan", "client_id", "client_email"] as const).filter(
    key => state.enabled[key] && !!state.values[key].trim()
  );
}

/**
 * Whether a form sends clients to review sites: any star question that isn't first-party only.
 * Bedrock and the public form treat a star question with no mode as hybrid, so that counts too.
 */
export function formUsesReviewSites(form: {
  formFields?: { fieldType: string; fieldOptions?: { collectionMode?: string } }[];
}) {
  return (form.formFields ?? []).some(
    field => field.fieldType === "star" && field.fieldOptions?.collectionMode !== "first_party_only"
  );
}

/**
 * "yes" or "no" when the route pins one form; "maybe" for defaults, since a region or branch
 * default can differ from the account-wide one.
 */
export type ReviewSites = "yes" | "no" | "maybe";

/** What a click does, in the order the client meets it: the form, the player, their record, the campaign. */
export function explainerSentences(
  state: BuilderState,
  context: { specificFormName?: string | null; reviewSites?: ReviewSites } = {}
): string[] {
  const sentences: string[] = [];
  const label = formTypeNoun(state.formType);
  const language = state.enabled.lang && state.values.lang.trim() ? ", in their language when the form has it" : "";

  if (state.formMode === "specific") {
    sentences.push(`The client opens ${context.specificFormName || "the form you choose"}${language}.`);
  } else if (state.playerMode === "none") {
    sentences.push(`The client opens your account-wide default ${label} form${language}.`);
  } else {
    sentences.push(
      `The client opens the ${label} form you've set as the default for the player's region or branch${language}.`
    );
  }

  if (state.playerMode === "sender") {
    const sender = state.destination === "website" ? "the page is for" : "who sent the email";
    sentences.push(
      `The response goes to the player ${sender}. If we can't match their email to a player, it arrives with no player.`
    );
  } else if (state.playerMode === "specific") {
    const player = state.specificPlayer;
    sentences.push(`The response goes to ${player ? player.fullName || player.email : "the player you choose"}.`);
  } else {
    sentences.push("The response arrives with no player.");
  }

  const reviewSites = context.reviewSites ?? "no";
  if (reviewSites !== "no") {
    if (state.playerMode === "none") {
      sentences.push("Without a player there are no review links to offer, so the review is collected on the form.");
    } else {
      const who = reviewSites === "maybe" ? "When the form asks for public reviews, clients" : "Clients";
      sentences.push(
        `${who} who rate the player 4 or 5 stars are offered that player's own Google, Zillow or Facebook review links. If the player has none connected, the review is collected on the form instead.`
      );
    }
  }

  const keys = activeClientKeys(state);
  const partner = state.clientType === "partner" ? " as a partner" : "";
  if (keys.length === 1 && keys[0] === "fan") {
    // A Fan ID that matches nothing never creates a Fan on its own
    sentences.push(
      "It's saved on the client's Fan record when the Fan ID matches one. Otherwise it arrives without a Fan, and the form asks for their name and email."
    );
  } else if (keys.length) {
    sentences.push(
      `It's saved on the client's Fan record. If they're new to SocialCoach, we create one${partner} and the form asks for their name.`
    );
  } else {
    sentences.push("It arrives without a Fan record, and the form asks the client for their name and email.");
  }

  if (keys.length) {
    sentences.push("If the client clicks again, they come back to the same form, so nothing is duplicated.");
  }

  const campaign = state.utm.campaign.trim();
  if (!campaign) {
    sentences.push("Add a campaign name in step 4 to see which email or page each response came from.");
  } else if (hasMergeField(campaign)) {
    sentences.push("Form Links and responses show the campaign your CRM fills in.");
  } else {
    sentences.push(`Form Links and responses show it came from the "${campaign}" campaign.`);
  }
  return sentences;
}

const STORAGE_PREFIX = "formRoutes.last.";

export function storageKey(coachId: number | string | undefined): string {
  return `${STORAGE_PREFIX}${coachId ?? "unknown"}`;
}

/**
 * Rebuilds a saved state: destination first, then the values. A restored value counts as touched
 * only when it differs from that destination's preset, so a later destination switch still
 * re-presets the fields the admin never edited.
 */
export function restoreState(saved: Partial<BuilderState> | null | undefined): BuilderState {
  const base = defaultState();
  if (!saved || typeof saved !== "object") return base;

  const destination: Destination = ["totalexpert", "crm", "website"].includes(saved.destination as string)
    ? (saved.destination as Destination)
    : base.destination;
  const preset = presetValues(destination);
  const utmPreset = presetUtm(destination);

  const values = { ...preset };
  const touched: BuilderState["touched"] = {};
  FIELD_KEYS.forEach(key => {
    const value = saved.values?.[key];
    if (typeof value === "string") {
      values[key] = value;
      if (value !== preset[key]) touched[key] = true;
    }
  });

  const utm = {
    source: typeof saved.utm?.source === "string" ? saved.utm.source : utmPreset.source,
    medium: typeof saved.utm?.medium === "string" ? saved.utm.medium : utmPreset.medium,
    campaign: typeof saved.utm?.campaign === "string" ? saved.utm.campaign : "",
  };
  if (utm.source !== utmPreset.source) touched.utm_source = true;
  if (utm.medium !== utmPreset.medium) touched.utm_medium = true;

  return {
    destination,
    formMode: saved.formMode === "specific" ? "specific" : "type",
    formType: saved.formType && BUILDER_FORM_TYPES.includes(saved.formType) ? saved.formType : base.formType,
    formGuid: typeof saved.formGuid === "string" ? saved.formGuid : null,
    playerMode: ["sender", "specific", "none"].includes(saved.playerMode as string)
      ? (saved.playerMode as BuilderState["playerMode"])
      : base.playerMode,
    specificPlayer: saved.specificPlayer?.email ? saved.specificPlayer : null,
    enabled: { ...base.enabled, ...(saved.enabled ?? {}) },
    values,
    touched,
    clientType: saved.clientType === "partner" ? "partner" : "customer",
    utm,
    codeId: typeof saved.codeId === "number" ? saved.codeId : null,
  };
}

function readSaved(key: string): BuilderState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? restoreState(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * `pinnedFormGuid` opens the builder on one specific form (from the Create Link dialog), on top of
 * the last route so the admin's destination and merge fields carry over.
 */
export function useFormRouteBuilder(coachId: number | string | undefined, pinnedFormGuid?: string | null) {
  const key = storageKey(coachId);
  const [initial] = useState(() => {
    const saved = readSaved(key);
    const state = saved ?? defaultState();
    if (pinnedFormGuid)
      return { state: { ...state, formMode: "specific" as const, formGuid: pinnedFormGuid }, restored: !!saved };
    return { state, restored: !!saved };
  });
  const [state, setState] = useState<BuilderState>(initial.state);
  const [restored, setRestored] = useState(initial.restored);

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Storage can be unavailable (private mode, blocked site data); the builder still works
    }
  }, [key, state]);

  const update = useCallback((patch: Partial<BuilderState>) => setState(prev => ({ ...prev, ...patch })), []);

  const setDestination = useCallback(
    (destination: Destination) => setState(prev => switchDestination(prev, destination)),
    []
  );

  const setValue = useCallback(
    (field: FieldKey, value: string) =>
      setState(prev => ({
        ...prev,
        values: { ...prev.values, [field]: value },
        touched: { ...prev.touched, [field]: true },
      })),
    []
  );

  const setEnabled = useCallback(
    (field: ClientFieldKey, enabled: boolean) =>
      setState(prev => ({ ...prev, enabled: { ...prev.enabled, [field]: enabled } })),
    []
  );

  const setUtm = useCallback(
    (field: "source" | "medium" | "campaign", value: string) =>
      setState(prev => ({
        ...prev,
        utm: { ...prev.utm, [field]: value },
        touched: field === "campaign" ? prev.touched : { ...prev.touched, [`utm_${field}`]: true },
      })),
    []
  );

  const pickExample = useCallback((example: ExampleKey) => setState(prev => applyExample(prev, example)), []);

  /** Back to the defaults, keeping the route code the admin picked. */
  const reset = useCallback(() => {
    setState(prev => ({ ...defaultState(), codeId: prev.codeId }));
    setRestored(false);
  }, []);

  return { state, restored, update, setDestination, setValue, setEnabled, setUtm, pickExample, reset };
}

export type FormRouteBuilder = ReturnType<typeof useFormRouteBuilder>;
